import { config } from '@config';
import * as Sentry from '@sentry/react';

export type AttachmentUploadFailurePhase =
  | 'cleanup_after_s3_failure'
  | 'complete_upload'
  | 'request_upload_url'
  | 's3_put';

type CaptureAttachmentUploadFailureParams = {
  attachmentId?: string;
  category?: string;
  contentType?: string;
  error: Error;
  fileSize?: number;
  phase: AttachmentUploadFailurePhase;
  status?: number;
  statusText?: string;
  targetId: string;
  targetType: 'answer' | 'product' | 'technology';
};

export const initializeSentry = () => {
  Sentry.init({
    dsn: config.sentry?.dsn,
    environment: config.app.runtimeEnvironment,
    sendDefaultPii: true,
    integrations: [
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      Sentry.replayCanvasIntegration(),
    ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
};

type CaptureSuggestionResolutionFailureParams = {
  error: unknown;
  logId: string;
  resolution: 'accept' | 'reject';
};

/**
 * Accept/reject telemetry is fire-and-forget: failures must never block the
 * user, but a systemic failure would silently corrupt the suggestion-quality
 * metrics, so report it to Sentry as a warning.
 */
export const captureSuggestionResolutionFailure = (params: CaptureSuggestionResolutionFailureParams): void => {
  const { error, logId, resolution } = params;

  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('feature', 'llm_chat');
    scope.setContext('suggestion_resolution_failure', {
      logId,
      resolution,
    });

    Sentry.captureException(error);
  });
};

export const captureAttachmentUploadFailure = (params: CaptureAttachmentUploadFailureParams): void => {
  const { attachmentId, category, contentType, error, fileSize, phase, status, statusText, targetId, targetType } =
    params;

  Sentry.withScope((scope) => {
    scope.setLevel('error');
    scope.setTag('feature', 'attachments');
    scope.setTag('phase', phase);
    scope.setTag('target_type', targetType);
    scope.setContext('attachment_upload_failure', {
      attachmentId,
      category,
      contentType,
      errorMessage: error.message,
      errorName: error.name,
      fileSize,
      phase,
      status,
      statusText,
      targetId,
      targetType,
    });

    Sentry.captureException(error);
  });
};
